import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import axios from 'axios';
import { config } from '../config';

interface User {
  username: string;
  email: string;
  userType: 'admin' | 'teacher' | 'parent' | 'student';
  attributes: Record<string, string>;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Idle timeout settings
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_TIME = 2 * 60 * 1000;  // Show warning 2 minutes before timeout

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const refreshUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      
      // Get user attributes from Cognito
      const attributes: Record<string, string> = currentUser.signInDetails?.loginId ? { email: currentUser.signInDetails.loginId } : {};
      
      console.log('🔍 AuthContext: Cognito user data:', {
        username: currentUser.username,
        attributes: attributes,
        signInDetails: currentUser.signInDetails
      });
      
      // Try to get user data from our backend
      try {
        const token = localStorage.getItem('cognitoToken');
        console.log('🔍 AuthContext: Token from localStorage:', token ? 'Found' : 'Not found');
        
          if (token) {
          console.log('🔍 AuthContext: Making API call to backend...');
          const response = await axios.get(`${config.BACKEND_URL}/api/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('🔍 AuthContext: Backend response:', response.data);
          
          // Validate that response is a real user object (not an HTML page or empty)
          const isValidUser = response.data &&
            typeof response.data === 'object' &&
            !Array.isArray(response.data) &&
            (response.data.id || response.data.user_type || response.data.userType);

          if (isValidUser) {
            // Handle both snake_case (user_type) and camelCase (userType)
            const userType = response.data.userType || response.data.user_type || 'parent';
            setUser({
              username: response.data.id,
              email: response.data.email,
              userType: userType,
              attributes: response.data
            });
            setLoading(false);
            return;
          } else {
            console.warn('🔍 AuthContext: Backend returned unexpected response shape, falling back to Cognito data');
            throw new Error('Invalid user response from backend');
          }
        } else {
          console.log('🔍 AuthContext: No token found in localStorage');
        }
      } catch (backendError: any) {
        console.log('🔍 AuthContext: Backend error:', backendError);
        
        // If backend auth fails, try to get user data by email without auth
        try {
          const email = attributes.email || currentUser.username;
          if (email) {
            console.log('🔍 AuthContext: Trying to get user data by email without auth...');
            // Try to get user directly by email
            const userResponse = await axios.get(`${config.BACKEND_URL}/api/test-user-by-email/${email}`);
            if (userResponse.data && userResponse.data.success) {
              const userData = userResponse.data.user;
              // Handle both snake_case and camelCase
              const userType = userData.userType || userData.user_type || 'parent';
              setUser({
                username: userData.id,
                email: userData.email,
                userType: userType as 'admin' | 'teacher' | 'parent' | 'student',
                attributes: userData
              });
              setLoading(false);
              return;
            }
          }
        } catch (fallbackError) {
          console.log('🔍 AuthContext: Fallback also failed, using Cognito data only');
        }
      }
      
      // Fallback to Cognito data only
      // Try to determine user type from Cognito groups or email
      let userType: 'admin' | 'teacher' | 'parent' | 'student' = 'parent'; // Default to parent
      const email = attributes.email || currentUser.username || '';
      const emailLower = email.toLowerCase();
      const usernameLower = (currentUser.username || '').toLowerCase();

      // Known admin emails — always force admin type
      const ADMIN_EMAILS = ['admin@oarayouthinnovationlab.ca'];
      if (ADMIN_EMAILS.includes(emailLower)) {
        userType = 'admin';
      } else if (emailLower.includes('teacher') || usernameLower.includes('teacher')) {
        userType = 'teacher';
      } else if (emailLower.includes('admin') || usernameLower.includes('admin')) {
        userType = 'admin';
      } else if (emailLower.includes('student') || usernameLower.includes('student')) {
        userType = 'student';
      }
      
      setUser({
        username: currentUser.username,
        email: email,
        userType: userType,
        attributes: attributes
      });
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      // Clear all timers
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      
      await amplifySignOut();
      localStorage.removeItem('cognitoToken');
      setUser(null);
      setShowIdleWarning(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Reset idle timer on user activity
  const resetIdleTimer = useCallback(() => {
    // Don't reset if not logged in
    if (!user) return;

    // Clear existing timers
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Hide warning if showing
    setShowIdleWarning(false);

    // Set warning timer (28 minutes - show warning 2 min before logout)
    warningTimerRef.current = setTimeout(() => {
      console.log('⚠️ Showing idle timeout warning (2 minutes remaining)');
      setShowIdleWarning(true);
      setRemainingTime(120); // 2 minutes in seconds
      
      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, IDLE_TIMEOUT - WARNING_TIME);

    // Set logout timer (30 minutes)
    idleTimerRef.current = setTimeout(async () => {
      console.log('⏰ Idle timeout reached - auto logging out');
      await signOut();
      alert('You have been logged out due to inactivity.');
      window.location.href = '/login';
    }, IDLE_TIMEOUT);
  }, [user]);

  // Continue session (dismiss warning)
  const continueSession = useCallback(() => {
    console.log('✅ User chose to continue session');
    resetIdleTimer();
  }, [resetIdleTimer]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer);
    });

    // Start initial timer
    resetIdleTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer);
      });
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [user, resetIdleTimer]);

  useEffect(() => {
    refreshUser();
  }, []);

  const value = {
    user,
    loading,
    signOut,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Idle Timeout Warning Modal */}
      {showIdleWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⏰</div>
            <h2 style={{ margin: '0 0 15px 0', color: '#dc3545' }}>Session Timeout Warning</h2>
            <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
              You will be automatically logged out in <strong style={{ color: '#dc3545', fontSize: '20px' }}>{Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')}</strong> due to inactivity.
            </p>
            <p style={{ fontSize: '14px', color: '#888', marginBottom: '25px' }}>
              Click "Stay Logged In" to continue your session.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={continueSession}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                ✅ Stay Logged In
              </button>
              <button
                onClick={signOut}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
